// API Routes
export const API_ROUTES = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    ME: '/auth/me',
    CHANGE_PASSWORD: '/auth/change-password',
  },
  USERS: '/users',
  LEADS: '/leads',
  CONTACTS: '/contacts',
  COMPANIES: '/companies',
  DEALS: '/deals',
  PIPELINES: '/pipelines',
  TASKS: '/tasks',
  ACTIVITIES: '/activities',
  NOTES: '/notes',
  EMAILS: '/emails',
  EXPENSES: '/expenses',
  EXPENSE_CATEGORIES: '/expense-categories',
  INVOICES: '/invoices',
  QUOTATIONS: '/quotations',
  PAYMENTS: '/payments',
  ACCOUNTS: '/accounts',
  TRANSACTIONS: '/transactions',
  BUDGETS: '/budgets',
  DASHBOARD: '/dashboard',
  FINANCIAL_REPORTS: '/financial/reports',
  NOTIFICATIONS: '/notifications',
  FILES: '/files',
  ORGANIZATION_SETTINGS: '/organization-settings',
  SIGNATURES: '/signatures',
  AGREEMENTS: '/agreements',
  AGREEMENT_TEMPLATES: '/agreement-templates',
  EMPLOYEES: '/employees',
  PAY_COMPONENTS: '/pay-components',
  SALARY_STRUCTURES: '/salary-structures',
  PAYSLIPS: '/payslips',
  PAYROLL_RUNS: '/payroll-runs',
  ME: '/me',
} as const;

// Color theme presets for the invoice template (12 curated dark palettes)
export const INVOICE_THEME_PRESETS = [
  { name: 'Cosmic Purple',   primary: '#010b17', accent: '#a855f7', highlight: '#22d3ee' },
  { name: 'Midnight Indigo', primary: '#0a0a23', accent: '#6366f1', highlight: '#818cf8' },
  { name: 'Onyx Cyan',       primary: '#0a0e1f', accent: '#06b6d4', highlight: '#67e8f9' },
  { name: 'Royal Gold',      primary: '#1e1b14', accent: '#d4a017', highlight: '#f5d76e' },
  { name: 'Espresso',        primary: '#1e1612', accent: '#92400e', highlight: '#fdba74' },
  { name: 'Crimson Noir',    primary: '#1a0a0a', accent: '#dc2626', highlight: '#fca5a5' },
  { name: 'Rose Vintage',    primary: '#1c0f1c', accent: '#ec4899', highlight: '#f9a8d4' },
  { name: 'Forest',          primary: '#0a2e1f', accent: '#16a34a', highlight: '#86efac' },
  { name: 'Emerald',         primary: '#052e2b', accent: '#059669', highlight: '#6ee7b7' },
  { name: 'Ocean Deep',      primary: '#0c2340', accent: '#1d4ed8', highlight: '#60a5fa' },
  { name: 'Charcoal',        primary: '#18181b', accent: '#525252', highlight: '#a3a3a3' },
  { name: 'Slate Pro',       primary: '#0f172a', accent: '#475569', highlight: '#94a3b8' },
] as const;

// App Config
export const APP_CONFIG = {
  APP_NAME: 'ZyFolks OperationsHub',
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  TOKEN_STORAGE_KEY: 'crm_refresh_token',
  SIDEBAR_STORAGE_KEY: 'crm_sidebar_collapsed',
  THEME_STORAGE_KEY: 'crm_theme',
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_FILE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/csv'],
} as const;

// Status Colors
export const STATUS_COLORS = {
  lead: {
    NEW: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    CONTACTED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    QUALIFIED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    UNQUALIFIED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
    CONVERTED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    LOST: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  },
  deal: {
    OPEN: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    WON: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    LOST: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    ABANDONED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  },
  task: {
    TODO: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
    IN_PROGRESS: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  },
  expense: {
    DRAFT: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
    PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    APPROVED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    REIMBURSED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  },
  invoice: {
    DRAFT: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
    SENT: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    VIEWED: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
    PARTIALLY_PAID: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    PAID: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    OVERDUE: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
    REFUNDED: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
  },
  quotation: {
    DRAFT: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
    SENT: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    PARTIALLY_INVOICED: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
    INVOICED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    PARTIALLY_PAID: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    PAID: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    EXPIRED: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    CONVERTED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  },
  agreement: {
    DRAFT: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
    SENT: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    SIGNED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    ACTIVE: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
    COMPLETED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  },
} as const;

// Priority Colors
export const PRIORITY_COLORS = {
  LOW: 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-300',
  MEDIUM: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  URGENT: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
} as const;

// Lead Sources
export const LEAD_SOURCES = [
  'WEBSITE',
  'REFERRAL',
  'SOCIAL_MEDIA',
  'COLD_CALL',
  'EMAIL_CAMPAIGN',
  'TRADE_SHOW',
  'PARTNER',
  'OTHER',
] as const;

// Currencies
export const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'] as const;

// Payment Methods
export const PAYMENT_METHODS = [
  'CASH',
  'BANK_TRANSFER',
  'CREDIT_CARD',
  'DEBIT_CARD',
  'CHECK',
  'ONLINE',
  'OTHER',
] as const;

// Account Types
export const ACCOUNT_TYPES = [
  'BANK',
  'CASH',
  'CREDIT_CARD',
  'ACCOUNTS_RECEIVABLE',
  'ACCOUNTS_PAYABLE',
] as const;

// Roles
export const ROLES = ['ADMIN', 'MANAGER', 'USER'] as const;
